/** Habits endpoints — thin wrappers around the FastAPI backend. */

import type { EffectiveAccess, SharedByView } from "./notes";
import { ApiClient } from "./client";
import type { TagResponse } from "./tags";

export type { TagResponse, EffectiveAccess, SharedByView };

/** Caller-relative scope for ``GET /habits``. ``"mine"`` returns only
 *  habits the caller owns, ``"shared"`` only habits shared *with* the
 *  caller, ``"all"`` (default) returns both. */
export type HabitsScope = "mine" | "shared" | "all";

export interface HabitResponse {
  id: string;
  user_id: string;
  name: string;
  description: string;
  color: string;
  target_per_day: number;
  tags: TagResponse[];
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  schedule_rrule: string | null;
  schedule_start_date: string | null;
  reminder_time: string | null;
  reminder_channel: string | null;
  /** Owner of the habit — same as ``user_id`` for backwards compat,
   *  surfaced explicitly so the UI doesn't conflate "the API caller"
   *  with "the owner". */
  owner_id: string;
  /** Non-null on habits shared *with* the caller. Null for habits
   *  the caller owns. */
  shared_by: SharedByView | null;
  /** Effective access the caller has on this row. */
  my_access: EffectiveAccess;
}

export interface HabitListResponse {
  habits: HabitResponse[];
}

export interface CheckInResponse {
  id: string;
  habit_id: string;
  user_id: string;
  occurred_at: string;
  count: number;
  note: string;
}

export interface CheckInListResponse {
  check_ins: CheckInResponse[];
}

/** Tri-state PATCH semantics:
 *
 * - field omitted → leave existing,
 * - field set to ``null`` → clear (only valid for nullable fields),
 * - concrete value → set.
 *
 * The wire layer only sends fields that are present in the object,
 * which lets clients construct partial patches without dragging in a
 * sentinel.
 */
export interface UpdateHabitPayload {
  name?: string;
  description?: string;
  color?: string;
  target_per_day?: number;
  tags?: string[];
  schedule_rrule?: string | null;
  schedule_start_date?: string | null;
  reminder_time?: string | null;
  reminder_channel?: "telegram" | null;
  archived?: boolean;
}

export interface CreateHabitPayload {
  name: string;
  description?: string;
  color?: string;
  target_per_day?: number;
  tags?: string[];
  schedule_rrule?: string | null;
  schedule_start_date?: string | null;
  reminder_time?: string | null;
  reminder_channel?: "telegram" | null;
}

export interface HabitStatsResponse {
  current_streak: number;
  longest_streak: number;
  completion_rate_30d: number;
  total_check_ins: number;
  last_checked_in_at: string | null;
  count_today: number;
  met_today: boolean;
}

export interface HabitDayResponse {
  date: string;
  count: number;
  target: number;
  met: boolean;
  scheduled: boolean;
}

export interface HabitHistoryResponse {
  days: HabitDayResponse[];
}

export const habitsApi = (client: ApiClient) => ({
  list(
    opts: { q?: string; include_archived?: boolean; scope?: HabitsScope } = {},
  ): Promise<HabitListResponse> {
    const query: Record<string, string | undefined> = {};
    if (opts.q) query.q = opts.q;
    if (opts.include_archived !== undefined) query.include_archived = String(opts.include_archived);
    if (opts.scope !== undefined) query.scope = opts.scope;
    return client.request<HabitListResponse>("/habits", { method: "GET", query });
  },
  get(habitId: string): Promise<HabitResponse> {
    return client.request<HabitResponse>(`/habits/${habitId}`, { method: "GET" });
  },
  create(payload: CreateHabitPayload): Promise<HabitResponse> {
    return client.request<HabitResponse>("/habits", { method: "POST", json: payload });
  },
  update(habitId: string, payload: UpdateHabitPayload): Promise<HabitResponse> {
    return client.request<HabitResponse>(`/habits/${habitId}`, {
      method: "PATCH",
      json: payload,
    });
  },
  delete(habitId: string): Promise<void> {
    return client.request<void>(`/habits/${habitId}`, { method: "DELETE" });
  },
  checkIn(habitId: string, count: number = 1): Promise<CheckInResponse> {
    return client.request<CheckInResponse>(`/habits/${habitId}/check-ins`, {
      method: "POST",
      json: { count },
    });
  },
  listCheckIns(
    habitId: string,
    range: { from: string; to: string },
  ): Promise<CheckInListResponse> {
    return client.request<CheckInListResponse>(`/habits/${habitId}/check-ins`, {
      method: "GET",
      query: { from: range.from, to: range.to },
    });
  },
  stats(habitId: string): Promise<HabitStatsResponse> {
    return client.request<HabitStatsResponse>(`/habits/${habitId}/stats`, {
      method: "GET",
    });
  },
  history(
    habitId: string,
    range: { from: string; to: string },
  ): Promise<HabitHistoryResponse> {
    return client.request<HabitHistoryResponse>(`/habits/${habitId}/history`, {
      method: "GET",
      query: { from: range.from, to: range.to },
    });
  },
});
